import 'chart.js/auto';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import "./PieChartWidget.scss";

interface IProps {
    data: any;
    config?: any;
    variables?: { [key: string]: string };
}

const LARGE_HEIGHT = 320;
const DEFAULT_COLORS = ["#98c3ff", "#ffdea5", "#ffd7d9", "#4b9f47", "#c3a8f5", "#a8d5b5"];

const PieChartWidget = (props: IProps) => {
    const data = Array.isArray(props.data) ? props.data : [];
    const isLarge = props.variables?.isLarge === "1";
    const isPercentage = props.variables?.isPercentage === "1";
    const showLegend = props.variables?.showLegend === "1";

    const normalizedValues: number[] = data.map((x: any) => {
        const n = Number(x.value);
        if (isPercentage && n > 0 && n < 1) return Math.round(n * 100);
        return n;
    });

    const total = normalizedValues.reduce((a, b) => a + b, 0);
    const sumsToHundred = isPercentage && total >= 99 && total <= 101;

    const customColors = props.variables?.sliceColor
        ? props.variables.sliceColor.split(",").map((c) => c.trim()).filter(Boolean)
        : null;
    const palette = customColors && customColors.length > 0 ? customColors : DEFAULT_COLORS;
    const getSliceColors = (count: number) =>
        Array.from({ length: count }, (_, i) => palette[i % palette.length]);

    return (
        <>
            {data.length > 0 && (
                <div className={`PieChartWidget${isLarge ? " large" : ""}`}>
                    <Pie
                        plugins={[ChartDataLabels as any]}
                        data={{
                            labels: data.map((x) => x.name || ""),
                            datasets: [{
                                data: normalizedValues,
                                backgroundColor: getSliceColors(data.length),
                                borderColor: "#ffffff",
                                borderWidth: 2,
                            }],
                        }}
                        height={isLarge ? LARGE_HEIGHT : undefined}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: {
                                padding: 20,
                            },
                            plugins: {
                                legend: {
                                    display: showLegend,
                                    position: "right" as const,
                                    labels: {
                                        boxWidth: 12,
                                        padding: 8,
                                    },
                                },
                                tooltip: {
                                    backgroundColor: '#3F4692',
                                    cornerRadius: 16,
                                    padding: 8,
                                    caretSize: 6,
                                    caretPadding: 6,
                                    displayColors: false,
                                    callbacks: {
                                        label: (tooltipItem) => {
                                            const rawValue = Number(tooltipItem.raw);
                                            const label = tooltipItem.label;

                                            if (!isPercentage) {
                                                return label
                                                    ? `${label}: ${tooltipItem.formattedValue}`
                                                    : tooltipItem.formattedValue;
                                            }

                                            if (sumsToHundred) {
                                                return label ? `${label}: ${rawValue}%` : `${rawValue}%`;
                                            }

                                            const pct = total > 0
                                                ? ((rawValue / total) * 100).toFixed(1)
                                                : "0";
                                            return label
                                                ? `${label}: ${rawValue} (${pct}%)`
                                                : `${rawValue} (${pct}%)`;
                                        },
                                        title: () => "",
                                    },
                                },
                                datalabels: {
                                    labels: {
                                        value: {
                                            anchor: "end",
                                            align: "start",
                                            color: "#000000",
                                            font: {
                                                size: 11,
                                            },
                                            formatter: (value: number) => {
                                                if (!isPercentage) return `${value}`;
                                                if (sumsToHundred) return `${value}%`;
                                                const pct = total > 0
                                                    ? ((value / total) * 100).toFixed(1)
                                                    : "0";
                                                return `${pct}%`;
                                            },
                                        },
                                        name: {
                                            display: !showLegend,
                                            anchor: "end",
                                            align: "end",
                                            color: "#000000",
                                            font: {
                                                size: 11,
                                            },
                                            formatter: (_value: number, ctx: any) => {
                                                const label = ctx.chart.data.labels?.[ctx.dataIndex];
                                                return label || "";
                                            },
                                        },
                                    },
                                },
                            },
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default PieChartWidget;